import { getWidgets, updateWidgets } from "api/dashboard";
import { DynamicDashboardAPI } from "api/types/apiTypes";
import { Loader } from "processhub-components-lib";
import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useAuth } from "./useAuth";

export const AppContext = createContext<any | null>(null);

const initialWidgets1 = [
  { id: "4390", tabs: [], position: 1 },
  { id: "8e65", tabs: [], position: 2 },
  { id: "33ec", tabs: [], position: 3 },
  { id: "ab5f", tabs: [], position: 4 },
  { id: "df7c", tabs: [], position: 5 },
];

const initialWidgets2 = [
  { id: "4370", tabs: [], position: 1 },
  { id: "8j65", tabs: [], position: 2 },
  { id: "33fc", tabs: [], position: 3 },
  { id: "ar5f", tabs: [], position: 4 },
  { id: "df3c", tabs: [], position: 5 },
];

export const AppProvider = ({ children }: { children: any }) => {
  const { isAuthenticated, isAuthLoading } = useAuth();

  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<any>(null);
  const [dateTime, setDateTime] = useState<any>(null);
  const [isScaled, setIsScaled] = useState<any>(null);

  /* ------------------ Tabs & Dashboard ------------------ */
  const [tabsData, setTabsData] = useState<any[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>("overview");

  const createDefaultDashboardData = () => ({
    title1: "Layout",
    title2: "Layout",
    title3: "Layout",
    selectedType: "init",
    widget1: initialWidgets1,
    widget2: initialWidgets2,
    widget3: [],
  });

  const buildDefaultOverviewTab = () => ({
    id: "overview",
    label: "Overview",
    type: "dashboard",
    data: JSON.stringify(createDefaultDashboardData()),
  });

  const [dashboardWidgets, setDashboardWidgets] = useState<any>({
    selectedType: "init",
    widget1: initialWidgets1,
    widget2: initialWidgets2,
    widget3: [],
  });
  const [dashboardWidgetsLoading, setDashboardWidgetsLoading] = useState(true);

  const isPersistingRef = useRef(false);
  const prevTabJsonRef = useRef<string>("[]");

  const updateDashboardWidgets = async (data: any) => {
    const activeTab = tabsData.find((t) => t.id === activeTabId);
    if (!activeTab || (activeTab.type !== "dashboard" && activeTab.type !== "widget")) return;

    setDashboardWidgets(data);
    const updatedTabs = tabsData.map((tab) =>
      tab.id === activeTabId ? { ...tab, data: JSON.stringify(data) } : tab
    );
    setTabsData(updatedTabs);
  };

  const getDashboardWidgets = async () => {
    setDashboardWidgetsLoading(true);
    try {
      const response: DynamicDashboardAPI = await getWidgets();
      if (response.status === 204 || !response.data?.dataJson) {
        const overviewTab = buildDefaultOverviewTab();
        await updateWidgets({ dataJson: JSON.stringify({ tabs: [overviewTab] }) });
        setTabsData([overviewTab]);
        setActiveTabId("overview");
        setDashboardWidgets(JSON.parse(overviewTab.data));
        setDashboardWidgetsLoading(false);
        return;
      }
      if (response.status === 200) {
        let parsed: any;
        try {
          parsed = JSON.parse(response.data.dataJson);
        } catch {
          parsed = null;
        }
        if (!parsed || !Array.isArray(parsed.tabs)) {
          const overviewTab = buildDefaultOverviewTab();
          await updateWidgets({ dataJson: JSON.stringify({ tabs: [overviewTab] }) });
          setTabsData([overviewTab]);
          setActiveTabId("overview");
          setDashboardWidgets(JSON.parse(overviewTab.data));
          setDashboardWidgetsLoading(false);
          return;
        }
        setTabsData(parsed.tabs);
        const defaultActiveId = parsed.tabs[0]?.id || "overview";
        setActiveTabId((prev) => parsed.tabs.some((t: any) => t.id === prev) ? prev : defaultActiveId);
      }
    } catch (error) {
      console.error("getDashboardWidgets error", error);
    }
    setDashboardWidgetsLoading(false);
  };

  /* ------------------ Effects ------------------ */
  useEffect(() => {
    if (isAuthenticated) getDashboardWidgets();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      const storedUserInfo = sessionStorage.getItem("auth_data");
      if (storedUserInfo) {
        const userData = JSON.parse(storedUserInfo).user;
        const possibleRoles: Record<string, string[]> = {
          admin: ["SuperAdmin", "Admin"],
          user: ["Manager", "Engineer", "Operator", "User"],
        };
        const userRoleRaw = userData.roles[0];

        const roleGroup = Object.keys(possibleRoles).find((key) =>
          possibleRoles[key].includes(userRoleRaw)
        );

        if (roleGroup) {
          setUserRole(roleGroup);
        } else {
          console.warn("Role does not match any group:", userRoleRaw);
        }
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (window.devicePixelRatio === 1.25) setIsScaled(true);
  }, []);

  useEffect(() => {
    localStorage.setItem("dateTime", dateTime);
  }, [dateTime]);

  useEffect(() => {
    const currentTab = tabsData.find((t) => t.id === activeTabId);
    if (!currentTab) return;
    if (currentTab.type === "dashboard" || currentTab.type === "widget") {
      let parsed: any;
      try {
        parsed = currentTab.data ? JSON.parse(currentTab.data) : null;
      } catch {
        parsed = null;
      }
      const REQUIRED_KEYS = ["selectedType", "widget1", "widget2", "widget3"];
      const isValid = parsed && typeof parsed === "object" && !REQUIRED_KEYS.some((k) => !(k in parsed));
      if (!isValid) {
        const fallback = createDefaultDashboardData();
        parsed = { ...fallback, ...(parsed || {}) };
        updateTabData(activeTabId, { data: JSON.stringify(parsed) });
      }
      setDashboardWidgets(parsed);
    }
  }, [tabsData, activeTabId]);
  
  useEffect(() => {
    if (isPersistingRef.current) return;
    const currentJson = JSON.stringify(tabsData);
    if (currentJson === prevTabJsonRef.current) return;
    const timeout = setTimeout(() => {
      prevTabJsonRef.current = currentJson;
      persistTabs(tabsData);
    }, 500);
    return () => clearTimeout(timeout);
  }, [tabsData]);

  /* ------------------ Tab Helpers ------------------ */
  const persistTabs = async (tabs: any[]) => {
    try {
      await updateWidgets({ dataJson: JSON.stringify({ tabs }) });
    } catch (err) {
      console.error("Failed to persist tabs", err);
    }
  };

  const addNewTab = async ({ label, type, selectedType, layoutTitles }: { label: string; type: string; selectedType?: number; layoutTitles?: string[] }) => {
    const newTabId = `tab-${Date.now()}`;
    let baseData = "";
    if (type !== "dxf-viewer") {
      const defaultData = createDefaultDashboardData();
      if (selectedType !== undefined) (defaultData as any).selectedType = selectedType;
      if (Array.isArray(layoutTitles)) {
        layoutTitles.forEach((t, idx) => {
          (defaultData as any)[`title${idx + 1}`] = t;
        });
      }
      baseData = JSON.stringify(defaultData);
    }
    const newTab: any = { id: newTabId, label, type, data: baseData, ...(type === "dxf-viewer" ? { comments: [], labels: [], widgets: [] } : {}) };
    const updated = [...tabsData, newTab];
    setTabsData(updated);
    setActiveTabId(newTabId);
    return newTabId;
  };

  const removeTab = async (tabId: string) => {
    const updated = tabsData.filter((t) => t.id !== tabId);
    setTabsData(updated);
    if (activeTabId === tabId) setActiveTabId(updated[0]?.id || "overview");
  };

  const updateTabData = async (tabId: string, options: { data?: any; comments?: any[]; label?: string; type?: string; labels?: any[]; widgets?: any[]; documents?: any[] }) => {
    const updated = tabsData.map((t) => {
      if (t.id !== tabId) return t;
      return {
        ...t,
        ...(options.label !== undefined ? { label: options.label } : {}),
        ...(options.type !== undefined ? { type: options.type } : {}),
        ...(options.data !== undefined ? { data: typeof options.data === "string" ? options.data : JSON.stringify(options.data) } : {}),
        ...(options.comments !== undefined ? { comments: options.comments } : {}),
        ...(options.labels !== undefined ? { labels: options.labels } : {}),
        ...(options.widgets !== undefined ? { widgets: options.widgets } : {}),
        ...(options.documents !== undefined ? { documents: options.documents } : {}),
      };
    });
    const updatedJson = JSON.stringify(updated);
    prevTabJsonRef.current = updatedJson;
    setTabsData(updated);
    await persistTabs(updated);
  };

  /* ------------------ Render ------------------ */
  if (isAuthLoading) {
    return (
      <div className="bg-black fixed inset-0 grid place-content-center">
        <Loader />
      </div>
    );
  }

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        dateTime,
        setDateTime,
        userRole,
        setUserRole,
        getDashboardWidgets,
        dashboardWidgets,
        setDashboardWidgets,
        dashboardWidgetsLoading,
        updateDashboardWidgets,
        isScaled,
        tabsData,
        setTabsData,
        activeTabId,
        setActiveTabId,
        addNewTab,
        removeTab,
        updateTabData,
        activeTab: tabsData.find((t: any) => t.id === activeTabId),
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default function useApp() {
  return useContext(AppContext);
}
